import React from "react";
import { MenuItem, Menu } from "@mui/material";

import { IOrderResponseItem } from "@screens/orders/order.typings";
import { ORDER_STATUS } from "@constants/order.constants";
import { USER_TYPE } from "@constants/user.constants";
import { canDeleteOrder, canEditOrder } from "./order-item.utils";
import {
  getCustomerOrderActionWindow,
  getOrderActionHelpMessage,
} from "@utils/order-action-window.utils";

interface IOrderItemMenuProps {
  anchorEl: Element;
  selectedItem: IOrderResponseItem | null;
  userType: string;
  isLoading: boolean;
  onClose(): void;
  onViewOrder(): void;
  onEditOrder(): void;
  onCancelOrder(): void;
  onDeleteOrder(): void;
  onContinueOrder(): void;
  onReOrder(): void;
}

export function OrderItemMenu({
  anchorEl,
  selectedItem,
  userType,
  isLoading,
  onViewOrder,
  onEditOrder,
  onReOrder,
  onCancelOrder,
  onDeleteOrder,
  onContinueOrder,
  onClose,
}: IOrderItemMenuProps) {
  if (!selectedItem) {
    return;
  }

  const isCanEditOrder = canEditOrder(selectedItem.orderStatus, userType);
  const isCustomer = userType === USER_TYPE.customer;
  const actionWindow = getCustomerOrderActionWindow(selectedItem);
  const isEditWindowClosed =
    isCustomer && isCanEditOrder && !actionWindow.canEdit;
  const isCanCancelOrder =
    selectedItem.orderStatus === ORDER_STATUS.new ||
    selectedItem.orderStatus === ORDER_STATUS.scheduled ||
    selectedItem.orderStatus === ORDER_STATUS.approved;
  const isCancelWindowClosed =
    isCustomer && isCanCancelOrder && !actionWindow.canCancel;
  const actionHelpMessage = getOrderActionHelpMessage({
    editBlocked: isEditWindowClosed,
    cancelBlocked: isCancelWindowClosed,
  });

  return (
    <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
      MenuListProps={{
        "aria-labelledby": "basic-button",
      }}
      className="menu-list-more">
      <MenuItem disabled={isLoading} onClick={onViewOrder}>
        View
      </MenuItem>

      {isCanEditOrder && (
        <MenuItem
          disabled={isLoading || isEditWindowClosed}
          onClick={onEditOrder}>
          Edit
        </MenuItem>
      )}

      {selectedItem.orderStatus === ORDER_STATUS.completed && userType == USER_TYPE.customer ? (
        <MenuItem disabled={isLoading} onClick={onReOrder}>
          Reorder
        </MenuItem>
      ) : (
        isCanCancelOrder && (
          <MenuItem
            disabled={isLoading || isCancelWindowClosed}
            onClick={onCancelOrder}>
            Cancel
          </MenuItem>
        )
      )}

      {selectedItem.orderStatus === ORDER_STATUS.draft && (
        <MenuItem disabled={isLoading} onClick={onContinueOrder}>
          Continue
        </MenuItem>
      )}

      {actionHelpMessage && (
        <MenuItem
          disabled
          sx={{
            color: "#7a5200 !important",
            fontSize: "0.75rem",
            maxWidth: "300px",
            opacity: "1 !important",
            whiteSpace: "normal",
          }}>
          {actionHelpMessage}
        </MenuItem>
      )}

      {canDeleteOrder(selectedItem.orderStatus, userType) && (
        <MenuItem disabled={isLoading} onClick={onDeleteOrder}>
          {selectedItem.orderStatus === ORDER_STATUS.draft &&
          userType === USER_TYPE.customer
            ? "Delete draft"
            : "Delete"}
        </MenuItem>
      )}
    </Menu>
  );
}
